import { type ReactNode, type RefObject, useContext, useState } from 'react';

import { useUserTrialDetails } from '@revelio/auth';
import type { MetricValues, SecondaryDimensionValues } from '@revelio/core';
import type { UploadFileResult } from '@revelio/data-access';
import type { ReplotChartNames } from '@revelio/replots';
import { useDisclosure } from '@revelio/ui';

import {
  isInspectorActive,
  useDataInspectorStore,
} from '../../../../../shared/data-inspector/data-inspector.store';
import { trackDataInspectorUsed } from '../../../../../shared/data-inspector/data-inspector-tracking';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { useToast } from '../../../hooks/useToast';
import { PptPreviewModal } from '../../Modal/PptPreviewModal';
import { useExportCover } from '../../PageActionBar/useExportCover';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import { useSharedReportData } from '../../SharedReport/SharedReportDataContext';
import { useSafeChart } from '../ChartProvider';
import { PlotLinkModal } from '../PlotLinkModal';
import { getExportTitle } from '../utils/getFileName';
import { useFileName } from '../utils/useFileName';
import {
  ChartActionMenuDropdown,
  type MenuOptions,
} from './ChartActionMenuDropdown';
import type { ShareState } from './ChartMenuContext';
import { DownloadPlotPNG } from './DownloadPlotPng';
import { onDownloadData } from './data-download/download-plot-data';
import {
  exportSingleChartToPpt,
  showExportToast,
  showUploadToast,
} from './data-download/ppt-export/ppt-exporter';
import { uploadFileToStorage } from './data-upload/upload-file';
import { ExpandModalContent, useOpenExpandModal } from './ExpandModal';
import { PageExportContext } from './PageExportProvider';

// Deliberately narrower than dash-meta's SHARED_PLOT_COMPONENTS: diverging
// bars and tables are not offered for sharing.
const SHAREABLE_COMPONENTS: ReadonlySet<string> = new Set([
  'AreaChart',
  'BarChart',
  'BoxPlot',
  'GaugeChartV2',
  'GroupedBarChart',
  'LineChart',
  'SankeyChart',
  'SingleMetric',
  'StackedBarChart',
  'TreeMap',
  'VolumeChart',
]);

export type ChartActionMenuProps = {
  menu?: MenuOptions[];
  title: string;
  /**
   * What the chart's dropdown is set to; export titles carry it. Read from
   * props rather than the export registry, which only updates after render.
   */
  selectionLabel?: string;
  dataType?: string;
  data?: unknown;
  shareState?: ShareState;
  plotChartType?: ChartType;
  primaryFilterId?: PrimaryFilterId;
  secondaryDimension?: SecondaryDimensionValues;
  metric?: MetricValues;
  pageName: string;
  slim?: boolean;
  printRef?: RefObject<HTMLDivElement>;
  expandModalContent?: ReactNode;
};

export const ChartActionMenu = ({
  menu = ['download-data'],
  title,
  selectionLabel,
  dataType,
  data,
  shareState,
  plotChartType,
  primaryFilterId,
  secondaryDimension,
  metric,
  pageName,
  slim,
  printRef,
  expandModalContent,
}: ChartActionMenuProps) => {
  const toast = useToast();
  const { isTrialUser } = useUserTrialDetails<boolean>();

  const { item } = useSafeChart();
  const pageExport = useContext(PageExportContext);
  const sharedReportData = useSharedReportData();
  const isDataInspectorEnabled = useDataInspectorStore(isInspectorActive);
  const openInspectorForChart = useDataInspectorStore(
    (state) => state.openForChart
  );

  const getRegistryExportInfo = () =>
    item?.id ? pageExport?.getChartRegistrySnapshot().get(item.id) : undefined;

  // Shareable once the chart's Replots component has saved its props to the
  // shared report store, and only for the chart types sharing supports.
  const capturedComponent = item?.id
    ? sharedReportData?.getAllChartData()[item.id]?.component
    : undefined;
  const isShareable =
    !!capturedComponent && SHAREABLE_COMPONENTS.has(capturedComponent);

  let notSharableReason: string | null = null;
  if (isTrialUser) {
    notSharableReason = 'Available on paid plans';
  } else if (!isShareable) {
    notSharableReason = 'Sharing is not available for this plot';
  }

  const defaultFileName = useFileName({ primaryFilterId, chartTitle: title });
  const exportTitle = getExportTitle({ pageName, primaryFilterId });
  // In this chart's context, so its own filter overrides show, as in its file
  // name.
  const cover = useExportCover();

  const handleDownloadData = () => {
    onDownloadData({
      title,
      dataType,
      data,
      primaryFilterId,
      secondaryDimension,
      metric,
      defaultFileName,
      pageName,
      chartId: item?.id,
      about: {
        pageTitle: cover.title || pageName,
        parameters: cover.parameters,
      },
    });
  };

  const openExpandModal = useOpenExpandModal();

  const {
    isOpen: isSharePlotModalOpen,
    onOpen: openSharePlotModal,
    onClose: closeSharePlotModal,
  } = useDisclosure();

  const {
    isOpen: isDownloadPNGModalOpen,
    onOpen: openDownloadPNGModal,
    onClose: closeDownloadPNGModal,
  } = useDisclosure();

  const handleDownloadPpt = async () => {
    try {
      const chartExportInfo = getRegistryExportInfo();
      const exportPromise = exportSingleChartToPpt(
        {
          title,
          exportTitle,
          chartType: shareState?.chartType as ReplotChartNames | undefined,
          plotChartType,
          selectedSegments: shareState?.chartProps?.selectedSegments as
            | { name: string }[]
            | undefined,
          data,
          dataType,
          gaugeMainText: chartExportInfo?.gaugeMainText,
          gaugeValue: chartExportInfo?.gaugeValue,
          gaugeColors: chartExportInfo?.gaugeColors,
          gaugeMinLabel: chartExportInfo?.gaugeMinLabel,
          gaugeMaxLabel: chartExportInfo?.gaugeMaxLabel,
          gaugeEntities: chartExportInfo?.gaugeEntities,
          captureRef: printRef ?? chartExportInfo?.captureRef,
          metric: metric ?? chartExportInfo?.metric,
          selectionLabel,
          boxEntityColors: chartExportInfo?.boxEntityColors,
          yAxisBounds: chartExportInfo?.yAxisBounds,
          cover,
          downloadName: defaultFileName,
        },
        { pageName, chartId: item?.id }
      ).getFile();

      showExportToast(toast, exportPromise);
    } catch (e) {
      console.error('Error exporting PPT:', e);
    }
  };

  const handleEditPpt = async () => {
    try {
      const uploadPromise = (async (): Promise<
        UploadFileResult | undefined
      > => {
        const chartExportInfo = getRegistryExportInfo();
        const result = await exportSingleChartToPpt(
          {
            title,
            exportTitle,
            chartType: shareState?.chartType as ReplotChartNames | undefined,
            plotChartType,
            selectedSegments: shareState?.chartProps?.selectedSegments as
              | { name: string }[]
              | undefined,
            data,
            dataType,
            gaugeMainText: chartExportInfo?.gaugeMainText,
            gaugeValue: chartExportInfo?.gaugeValue,
            gaugeColors: chartExportInfo?.gaugeColors,
            gaugeMinLabel: chartExportInfo?.gaugeMinLabel,
            gaugeMaxLabel: chartExportInfo?.gaugeMaxLabel,
            gaugeEntities: chartExportInfo?.gaugeEntities,
            selectionLabel,
            boxEntityColors: chartExportInfo?.boxEntityColors,
            yAxisBounds: chartExportInfo?.yAxisBounds,
            cover,
            downloadName: defaultFileName,
          },
          { pageName, chartId: item?.id }
        ).getBlob();
        if (!result) return;
        return await uploadFileToStorage(result);
      })();

      showUploadToast(toast, uploadPromise);

      const uploadResult = await uploadPromise;
      if (uploadResult) {
        setPreviewUrl(uploadResult.previewUrl);
        setDownloadUrl(uploadResult.downloadUrl);
        setEditUrl(uploadResult.editUrl);
        openPreview();
      }
    } catch (e) {
      console.error('Error previewing PPT:', e);
    }
  };

  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);
  const [editUrl, setEditUrl] = useState<string | undefined>(undefined);
  const {
    isOpen: isPreviewOpen,
    onOpen: openPreview,
    onClose: closePreview,
  } = useDisclosure();

  return (
    <>
      <ChartActionMenuDropdown
        menu={
          openExpandModal
            ? menu
            : menu.filter((option) => option !== 'expand-plot')
        }
        slim={slim}
        notSharableReason={notSharableReason}
        downloadDataDisabled={isTrialUser}
        downloadPNGDisabled={isTrialUser}
        downloadPPTDisabled={isTrialUser}
        previewPPTDisabled={isTrialUser}
        onExpandPlotClick={() => openExpandModal?.(title)}
        onSharePlotClick={openSharePlotModal}
        onDownloadDataClick={handleDownloadData}
        onDownloadPNGClick={openDownloadPNGModal}
        onDownloadPPTClick={handleDownloadPpt}
        onPreviewPPTClick={handleEditPpt}
        onInspectDataClick={
          isDataInspectorEnabled
            ? () => {
                trackDataInspectorUsed('opened_from_chart', {
                  ...(item?.id !== undefined && { chart_id: item.id }),
                });
                openInspectorForChart(item?.id ?? null);
              }
            : undefined
        }
      />
      <ExpandModalContent title={title}>
        {expandModalContent}
      </ExpandModalContent>

      <PlotLinkModal
        plotLinkModalState={{
          isPlotLinkOpen: isSharePlotModalOpen,
          onPlotLinkOpen: openSharePlotModal,
          onPlotLinkClose: closeSharePlotModal,
        }}
        pageName={pageName}
        title={title}
      />

      {printRef && (
        <DownloadPlotPNG
          isOpen={isDownloadPNGModalOpen}
          onClose={closeDownloadPNGModal}
          title={title}
          selectionLabel={selectionLabel}
          printRef={printRef}
          defaultFileName={defaultFileName}
          pageName={pageName}
          chartId={item?.id}
          chartElement={expandModalContent}
        />
      )}

      <PptPreviewModal
        isOpen={isPreviewOpen}
        onClose={closePreview}
        title={defaultFileName || title}
        previewUrl={previewUrl}
        downloadUrl={downloadUrl}
        editUrl={editUrl}
      />
    </>
  );
};
